import { DecimalPipe } from '@angular/common';
import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TabsModule } from 'primeng/tabs';
import { LocalizationService } from '../../core/localization/localization.service';
import { CurrencySelectorComponent } from '../../shared/design-system/currency-selector.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import {
  CatalogService,
  HospitalityCancellationPolicy,
  HospitalityChannelRateRule,
  HospitalityOverbookingPolicy,
  HospitalityProperty,
  HospitalityRatePlan,
  HospitalityRoomType,
  HospitalityRoomUnit
} from './catalog.service';

type PropertyCreateDialogMode = 'roomType' | 'room' | 'ratePlan' | 'channelRule';

@Component({
  standalone: true,
  selector: 'ain-hospitality-property-detail',
  imports: [
    DecimalPipe, AinPageComponent, AinStateComponent, RouterLink, FormsModule,
    ButtonModule, InputTextModule, InputNumberModule, CheckboxModule, TableModule, TabsModule,
    DialogModule, SelectModule, CurrencySelectorComponent
  ],
  template: `
    <ain-page
      [title]="isCreateMode() ? t('catalog.hospitality.properties.createTitle') : t('catalog.hospitality.properties.detailTitle')"
      [description]="t('catalog.hospitality.properties.detailDescription')">
      @if (loading()) {
        <ain-state type="loading" [title]="t('catalog.loading')" />
      } @else if (!isCreateMode() && !property()) {
        <ain-state type="error" [title]="t('catalog.unavailable')" [message]="error()" />
      } @else {
        <section class="header-card">
          <div>
            <h3>{{ isCreateMode() ? t('catalog.hospitality.properties.newRecord') : property()!.name }}</h3>
            <p>{{ isCreateMode() ? t('catalog.hospitality.properties.createHint') : property()!.propertyCode }}</p>
          </div>
          <div class="actions">
            <a [routerLink]="['/hospitality/properties']">{{ t('catalog.action.backToList') }}</a>
            @if (isCreateMode() || editing()) {
              <button pButton type="button" [label]="t('catalog.action.save')" [disabled]="saving()" (click)="saveProperty()"></button>
            } @else {
              <button pButton type="button" [label]="t('catalog.action.edit')" severity="secondary" (click)="startEdit()"></button>
            }
          </div>
        </section>

        @if (isCreateMode() || editing()) {
          <section class="form-grid">
            @if (isCreateMode()) {
              <label><span>{{ t('catalog.hospitality.propertyCode') }}</span><input pInputText [(ngModel)]="form.propertyCode" /></label>
            }
            <label><span>{{ t('catalog.hospitality.propertyName') }}</span><input pInputText [(ngModel)]="form.name" /></label>
            <label class="wide"><span>{{ t('catalog.hospitality.address') }}</span><input pInputText [(ngModel)]="form.address" /></label>
            <label><span>{{ t('catalog.hospitality.timeZone') }}</span><input pInputText [(ngModel)]="form.timeZoneId" /></label>
            @if (!isCreateMode()) {
              <label class="checkbox-row"><p-checkbox [(ngModel)]="form.isActive" [binary]="true" /> {{ t('catalog.hospitality.active') }}</label>
            }
          </section>
        }

        @if (!isCreateMode() && property()) {
          <p-tabs value="0">
            <p-tablist>
              <p-tab value="0">{{ t('catalog.hospitality.tabs.roomTypes') }}</p-tab>
              <p-tab value="1">{{ t('catalog.hospitality.tabs.rooms') }}</p-tab>
              <p-tab value="2">{{ t('catalog.hospitality.tabs.ratePlans') }}</p-tab>
              <p-tab value="3">{{ t('catalog.hospitality.tabs.policies') }}</p-tab>
              <p-tab value="4">{{ t('catalog.hospitality.tabs.channelRules') }}</p-tab>
            </p-tablist>
            <p-tabpanels>
              <p-tabpanel value="0">
                <section class="panel">
                  <div class="toolbar">
                    <button pButton type="button" [label]="t('catalog.hospitality.action.addRoomType')" (click)="openRoomTypeDialog()"></button>
                  </div>
                  <p-table [value]="roomTypes()">
                    <ng-template pTemplate="header">
                      <tr>
                        <th>{{ t('catalog.hospitality.roomTypeCode') }}</th>
                        <th>{{ t('catalog.hospitality.roomTypeName') }}</th>
                        <th>{{ t('catalog.hospitality.maxOccupancy') }}</th>
                        <th>{{ t('catalog.hospitality.nightlyRate') }}</th>
                      </tr>
                    </ng-template>
                    <ng-template pTemplate="body" let-row>
                      <tr>
                        <td>{{ row.typeCode }}</td>
                        <td>{{ row.name }}</td>
                        <td>{{ row.maxOccupancy }}</td>
                        <td>{{ row.baseNightlyRate | number:'1.2-2' }}</td>
                      </tr>
                    </ng-template>
                  </p-table>
                </section>
              </p-tabpanel>
              <p-tabpanel value="1">
                <section class="panel">
                  <div class="toolbar">
                    <button pButton type="button" [label]="t('catalog.hospitality.action.addRoom')" [disabled]="!roomTypes().length" (click)="openRoomDialog()"></button>
                  </div>
                  <p-table [value]="rooms()">
                    <ng-template pTemplate="header">
                      <tr>
                        <th>{{ t('catalog.hospitality.roomNumber') }}</th>
                        <th>{{ t('catalog.hospitality.floor') }}</th>
                        <th>{{ t('catalog.tracking.status') }}</th>
                      </tr>
                    </ng-template>
                    <ng-template pTemplate="body" let-row>
                      <tr>
                        <td>{{ row.roomNumber }}</td>
                        <td>{{ row.floor || '—' }}</td>
                        <td>{{ row.status }}</td>
                      </tr>
                    </ng-template>
                  </p-table>
                </section>
              </p-tabpanel>
              <p-tabpanel value="2">
                <section class="panel">
                  <div class="toolbar">
                    <button pButton type="button" [label]="t('catalog.hospitality.action.addRatePlan')" (click)="openRatePlanDialog()"></button>
                    <a [routerLink]="['/hospitality/rate-calendar']">{{ t('catalog.hospitality.rateCalendar.title') }}</a>
                  </div>
                  <p-table [value]="ratePlans()">
                    <ng-template pTemplate="header">
                      <tr>
                        <th>{{ t('catalog.hospitality.ratePlanCode') }}</th>
                        <th>{{ t('catalog.hospitality.ratePlanName') }}</th>
                        <th>{{ t('catalog.hospitality.currency') }}</th>
                      </tr>
                    </ng-template>
                    <ng-template pTemplate="body" let-row>
                      <tr>
                        <td>{{ row.planCode }}</td>
                        <td>{{ row.name }}</td>
                        <td>{{ row.currencyCode }}</td>
                      </tr>
                    </ng-template>
                  </p-table>
                </section>
              </p-tabpanel>
              <p-tabpanel value="3">
                <section class="panel policy-grid">
                  @if (cancellationPolicy()) {
                    <article>
                      <h4>{{ t('catalog.hospitality.policies.cancellation') }}</h4>
                      <label><span>{{ t('catalog.hospitality.policies.freeCancelHours') }}</span><p-inputNumber [(ngModel)]="cancellationForm.freeCancelHoursBeforeCheckIn" [min]="0" /></label>
                      <label><span>{{ t('catalog.hospitality.policies.cancellationFeePercent') }}</span><p-inputNumber [(ngModel)]="cancellationForm.cancellationFeePercent" [min]="0" [max]="100" /></label>
                      <label><span>{{ t('catalog.hospitality.policies.noShowFeePercent') }}</span><p-inputNumber [(ngModel)]="cancellationForm.noShowFeePercent" [min]="0" [max]="100" /></label>
                      <label class="checkbox-row"><p-checkbox [(ngModel)]="cancellationForm.isEnabled" [binary]="true" /> {{ t('catalog.hospitality.policies.enabled') }}</label>
                      <button pButton type="button" [label]="t('catalog.action.save')" (click)="saveCancellationPolicy()"></button>
                    </article>
                  }
                  @if (overbookingPolicy()) {
                    <article>
                      <h4>{{ t('catalog.hospitality.policies.overbooking') }}</h4>
                      <label><span>{{ t('catalog.hospitality.policies.allowedOverbookPercent') }}</span><p-inputNumber [(ngModel)]="overbookingForm.allowedOverbookPercent" [min]="0" [max]="100" /></label>
                      <label class="checkbox-row"><p-checkbox [(ngModel)]="overbookingForm.isEnabled" [binary]="true" /> {{ t('catalog.hospitality.policies.enabled') }}</label>
                      <button pButton type="button" [label]="t('catalog.action.save')" (click)="saveOverbookingPolicy()"></button>
                    </article>
                  }
                </section>
              </p-tabpanel>
              <p-tabpanel value="4">
                <section class="panel">
                  <div class="toolbar">
                    <button pButton type="button" [label]="t('catalog.hospitality.action.addChannelRule')" [disabled]="!ratePlans().length" (click)="openChannelRuleDialog()"></button>
                  </div>
                  <p-table [value]="channelRules()">
                    <ng-template pTemplate="header">
                      <tr>
                        <th>{{ t('catalog.hospitality.channelCode') }}</th>
                        <th>{{ t('catalog.hospitality.ratePlan') }}</th>
                        <th>{{ t('catalog.hospitality.channelAdjustment') }}</th>
                        <th>{{ t('catalog.hospitality.allocatedRooms') }}</th>
                        <th>{{ t('catalog.tracking.status') }}</th>
                      </tr>
                    </ng-template>
                    <ng-template pTemplate="body" let-row>
                      <tr>
                        <td>{{ row.channelCode }}</td>
                        <td>{{ ratePlanLabel(row.ratePlanId) }}</td>
                        <td>{{ row.rateAdjustmentPercent | number:'1.1-1' }}%</td>
                        <td>{{ row.allocatedRooms ?? '—' }}</td>
                        <td>{{ row.isActive ? t('catalog.hospitality.active') : t('catalog.hospitality.inactive') }}</td>
                      </tr>
                    </ng-template>
                  </p-table>
                </section>
              </p-tabpanel>
            </p-tabpanels>
          </p-tabs>
        }
      }

      <p-dialog
        [header]="dialogTitle()"
        [visible]="dialogOpen()"
        [modal]="true"
        [style]="{ width: '32rem', maxWidth: '95vw' }"
        (visibleChange)="onDialogVisibleChange($event)">
        @if (dialogMode() === 'roomType') {
          <div class="dialog-form">
            <label><span>{{ t('catalog.hospitality.roomTypeCode') }}</span><input pInputText [(ngModel)]="roomTypeForm.typeCode" /></label>
            <label><span>{{ t('catalog.hospitality.roomTypeName') }}</span><input pInputText [(ngModel)]="roomTypeForm.name" /></label>
            <label><span>{{ t('catalog.hospitality.maxOccupancy') }}</span><p-inputNumber [(ngModel)]="roomTypeForm.maxOccupancy" [min]="1" /></label>
            <label><span>{{ t('catalog.hospitality.nightlyRate') }}</span><p-inputNumber [(ngModel)]="roomTypeForm.baseNightlyRate" [min]="0" /></label>
          </div>
        } @else if (dialogMode() === 'room') {
          <div class="dialog-form">
            <label><span>{{ t('catalog.hospitality.roomType') }}</span>
              <p-select
                [options]="roomTypeSelectOptions()"
                [(ngModel)]="roomForm.roomTypeId"
                optionLabel="label"
                optionValue="value"
                [filter]="true"
                appendTo="body" />
            </label>
            <label><span>{{ t('catalog.hospitality.roomNumber') }}</span><input pInputText [(ngModel)]="roomForm.roomNumber" /></label>
            <label><span>{{ t('catalog.hospitality.floor') }}</span><input pInputText [(ngModel)]="roomForm.floor" /></label>
          </div>
        } @else if (dialogMode() === 'ratePlan') {
          <div class="dialog-form">
            <label><span>{{ t('catalog.hospitality.ratePlanCode') }}</span><input pInputText [(ngModel)]="ratePlanForm.planCode" /></label>
            <label><span>{{ t('catalog.hospitality.ratePlanName') }}</span><input pInputText [(ngModel)]="ratePlanForm.name" /></label>
            <label><span>{{ t('catalog.hospitality.currency') }}</span>
              <ain-currency-selector [(ngModel)]="ratePlanForm.currencyCode" [showSearch]="true" />
            </label>
          </div>
        } @else if (dialogMode() === 'channelRule') {
          <div class="dialog-form">
            <label><span>{{ t('catalog.hospitality.channelCode') }}</span><input pInputText [(ngModel)]="channelRuleForm.channelCode" /></label>
            <label><span>{{ t('catalog.hospitality.ratePlan') }}</span>
              <p-select
                [options]="ratePlanSelectOptions()"
                [(ngModel)]="channelRuleForm.ratePlanId"
                optionLabel="label"
                optionValue="value"
                [filter]="true"
                appendTo="body" />
            </label>
            <label><span>{{ t('catalog.hospitality.channelAdjustment') }}</span><p-inputNumber [(ngModel)]="channelRuleForm.rateAdjustmentPercent" /></label>
            <label><span>{{ t('catalog.hospitality.allocatedRooms') }}</span><p-inputNumber [(ngModel)]="channelRuleForm.allocatedRooms" [min]="0" /></label>
          </div>
        }

        <div class="dialog-footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="closeDialog()"></button>
          <button pButton type="button" [label]="t('catalog.action.save')" [disabled]="dialogSaving()" (click)="submitDialog()"></button>
        </div>
      </p-dialog>
    </ain-page>
  `,
  styles: [`
    .header-card { display: flex; justify-content: space-between; gap: 16px; margin-bottom: 16px; padding: 16px; border: 1px solid #e5e7eb; border-radius: 8px; }
    .actions { display: flex; gap: 12px; align-items: center; }
    .form-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px; margin-bottom: 16px; }
    .form-grid label { display: grid; gap: 6px; }
    .form-grid .wide { grid-column: span 3; }
    .checkbox-row { display: flex; align-items: center; gap: 8px; }
    .panel { display: grid; gap: 10px; margin-top: 12px; }
    .policy-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px; }
    .policy-grid article { display: grid; gap: 8px; padding: 12px; border: 1px solid #e5e7eb; border-radius: 8px; }
    .toolbar { display: flex; gap: 8px; align-items: center; }
    .dialog-form { display: grid; gap: 12px; }
    .dialog-form label { display: grid; gap: 6px; }
    .dialog-form span { font-size: 0.85rem; font-weight: 600; color: #475569; }
    .dialog-footer { display: flex; justify-content: flex-end; gap: 8px; margin-top: 16px; }
    @media (max-width: 760px) { .form-grid { grid-template-columns: 1fr; } .form-grid .wide { grid-column: span 1; } }
  `]
})
export class HospitalityPropertyDetailComponent implements OnInit {
  private readonly service = inject(CatalogService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);

  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly editing = signal(false);
  readonly dialogOpen = signal(false);
  readonly dialogSaving = signal(false);
  readonly dialogMode = signal<PropertyCreateDialogMode | null>(null);
  readonly property = signal<HospitalityProperty | null>(null);
  readonly roomTypes = signal<HospitalityRoomType[]>([]);
  readonly rooms = signal<HospitalityRoomUnit[]>([]);
  readonly ratePlans = signal<HospitalityRatePlan[]>([]);
  readonly channelRules = signal<HospitalityChannelRateRule[]>([]);
  readonly cancellationPolicy = signal<HospitalityCancellationPolicy | null>(null);
  readonly overbookingPolicy = signal<HospitalityOverbookingPolicy | null>(null);
  readonly error = signal('');

  cancellationForm = { freeCancelHoursBeforeCheckIn: 24, cancellationFeePercent: 0, noShowFeePercent: 100, isEnabled: true };
  overbookingForm = { allowedOverbookPercent: 0, isEnabled: false };

  readonly isCreateMode = computed(() => this.route.snapshot.paramMap.get('id') === 'new');

  readonly dialogTitle = computed(() => {
    switch (this.dialogMode()) {
      case 'roomType': return this.t('catalog.hospitality.action.addRoomType');
      case 'room': return this.t('catalog.hospitality.action.addRoom');
      case 'ratePlan': return this.t('catalog.hospitality.action.addRatePlan');
      case 'channelRule': return this.t('catalog.hospitality.action.addChannelRule');
      default: return '';
    }
  });

  readonly roomTypeSelectOptions = computed(() =>
    this.roomTypes().map(rt => ({ label: `${rt.typeCode} — ${rt.name}`, value: rt.id }))
  );

  readonly ratePlanSelectOptions = computed(() =>
    this.ratePlans().map(rp => ({ label: `${rp.planCode} — ${rp.name}`, value: rp.id }))
  );

  form = {
    propertyCode: '',
    name: '',
    address: '',
    timeZoneId: 'UTC',
    isActive: true
  };

  roomTypeForm = { typeCode: '', name: '', maxOccupancy: 2, baseNightlyRate: 100 };
  roomForm = { roomTypeId: '', roomNumber: '', floor: '' };
  ratePlanForm = { planCode: '', name: '', currencyCode: 'IRR' };
  channelRuleForm = { channelCode: '', ratePlanId: '', rateAdjustmentPercent: 0, allocatedRooms: null as number | null };

  ngOnInit(): void {
    if (this.isCreateMode()) {
      this.loading.set(false);
      return;
    }
    const id = this.route.snapshot.paramMap.get('id');
    if (id) this.loadProperty(id);
  }

  loadProperty(id: string): void {
    this.loading.set(true);
    this.service.getHospitalityProperty(id).subscribe({
      next: property => {
        this.property.set(property);
        this.form = {
          propertyCode: property.propertyCode,
          name: property.name,
          address: property.address,
          timeZoneId: property.timeZoneId,
          isActive: property.isActive
        };
        this.loadRoomData(id);
        this.loadExtendedData(id);
        this.loading.set(false);
      },
      error: () => {
        this.error.set(this.t('catalog.hospitality.propertyNotFound'));
        this.loading.set(false);
      }
    });
  }

  loadRoomData(propertyId: string): void {
    this.service.listHospitalityRoomTypes(propertyId).subscribe({
      next: rows => this.roomTypes.set(rows),
      error: () => this.roomTypes.set([])
    });
    this.service.listHospitalityRooms(propertyId).subscribe({
      next: rows => this.rooms.set(rows),
      error: () => this.rooms.set([])
    });
  }

  loadExtendedData(propertyId: string): void {
    this.service.listHospitalityRatePlans(propertyId).subscribe({
      next: rows => this.ratePlans.set(rows),
      error: () => this.ratePlans.set([])
    });
    this.service.listChannelRateRules(propertyId).subscribe({
      next: rows => this.channelRules.set(rows),
      error: () => this.channelRules.set([])
    });
    this.service.getHospitalityCancellationPolicy(propertyId).subscribe({
      next: policy => {
        this.cancellationPolicy.set(policy);
        this.cancellationForm = {
          freeCancelHoursBeforeCheckIn: policy.freeCancelHoursBeforeCheckIn,
          cancellationFeePercent: policy.cancellationFeePercent,
          noShowFeePercent: policy.noShowFeePercent,
          isEnabled: policy.isEnabled
        };
      },
      error: () => this.cancellationPolicy.set(null)
    });
    this.service.getHospitalityOverbookingPolicy(propertyId).subscribe({
      next: policy => {
        this.overbookingPolicy.set(policy);
        this.overbookingForm = {
          allowedOverbookPercent: policy.allowedOverbookPercent,
          isEnabled: policy.isEnabled
        };
      },
      error: () => this.overbookingPolicy.set(null)
    });
  }

  ratePlanLabel(ratePlanId: string): string {
    const plan = this.ratePlans().find(item => item.id === ratePlanId);
    return plan ? `${plan.planCode} — ${plan.name}` : ratePlanId;
  }

  openRoomTypeDialog(): void {
    this.roomTypeForm = { typeCode: '', name: '', maxOccupancy: 2, baseNightlyRate: 100 };
    this.dialogMode.set('roomType');
    this.dialogOpen.set(true);
  }

  openRoomDialog(): void {
    const types = this.roomTypes();
    this.roomForm = {
      roomTypeId: types[0]?.id ?? '',
      roomNumber: '',
      floor: ''
    };
    this.dialogMode.set('room');
    this.dialogOpen.set(true);
  }

  openRatePlanDialog(): void {
    this.ratePlanForm = { planCode: '', name: '', currencyCode: 'IRR' };
    this.dialogMode.set('ratePlan');
    this.dialogOpen.set(true);
  }

  openChannelRuleDialog(): void {
    const plans = this.ratePlans();
    this.channelRuleForm = {
      channelCode: '',
      ratePlanId: plans[0]?.id ?? '',
      rateAdjustmentPercent: 0,
      allocatedRooms: null
    };
    this.dialogMode.set('channelRule');
    this.dialogOpen.set(true);
  }

  onDialogVisibleChange(visible: boolean): void {
    if (!visible) this.closeDialog();
  }

  closeDialog(): void {
    this.dialogOpen.set(false);
    this.dialogMode.set(null);
    this.dialogSaving.set(false);
  }

  submitDialog(): void {
    const propertyId = this.property()?.id;
    if (!propertyId) return;

    const mode = this.dialogMode();
    if (!mode) return;

    this.dialogSaving.set(true);

    if (mode === 'roomType') {
      if (!this.roomTypeForm.typeCode.trim() || !this.roomTypeForm.name.trim()) {
        this.dialogSaving.set(false);
        return;
      }
      this.service.createHospitalityRoomType({
        propertyId,
        typeCode: this.roomTypeForm.typeCode.trim(),
        name: this.roomTypeForm.name.trim(),
        maxOccupancy: this.roomTypeForm.maxOccupancy,
        baseNightlyRate: this.roomTypeForm.baseNightlyRate
      }).subscribe({
        next: () => {
          this.loadRoomData(propertyId);
          this.closeDialog();
        },
        error: () => this.dialogSaving.set(false)
      });
      return;
    }

    if (mode === 'room') {
      if (!this.roomForm.roomTypeId || !this.roomForm.roomNumber.trim()) {
        this.dialogSaving.set(false);
        return;
      }
      this.service.createHospitalityRoom({
        propertyId,
        roomTypeId: this.roomForm.roomTypeId,
        roomNumber: this.roomForm.roomNumber.trim(),
        floor: this.roomForm.floor.trim() || null
      }).subscribe({
        next: () => {
          this.loadRoomData(propertyId);
          this.closeDialog();
        },
        error: () => this.dialogSaving.set(false)
      });
      return;
    }

    if (mode === 'ratePlan') {
      if (!this.ratePlanForm.planCode.trim() || !this.ratePlanForm.name.trim()) {
        this.dialogSaving.set(false);
        return;
      }
      this.service.createHospitalityRatePlan({
        propertyId,
        planCode: this.ratePlanForm.planCode.trim(),
        name: this.ratePlanForm.name.trim(),
        currencyCode: this.ratePlanForm.currencyCode || 'IRR'
      }).subscribe({
        next: () => {
          this.loadExtendedData(propertyId);
          this.closeDialog();
        },
        error: () => this.dialogSaving.set(false)
      });
      return;
    }

    if (mode === 'channelRule') {
      if (!this.channelRuleForm.channelCode.trim() || !this.channelRuleForm.ratePlanId) {
        this.dialogSaving.set(false);
        return;
      }
      this.service.createChannelRateRule({
        propertyId,
        ratePlanId: this.channelRuleForm.ratePlanId,
        channelCode: this.channelRuleForm.channelCode.trim(),
        rateAdjustmentPercent: this.channelRuleForm.rateAdjustmentPercent,
        allocatedRooms: this.channelRuleForm.allocatedRooms
      }).subscribe({
        next: () => {
          this.loadExtendedData(propertyId);
          this.closeDialog();
        },
        error: () => this.dialogSaving.set(false)
      });
    }
  }

  saveCancellationPolicy(): void {
    const propertyId = this.property()?.id;
    if (!propertyId) return;
    this.service.updateHospitalityCancellationPolicy(propertyId, this.cancellationForm).subscribe({
      next: policy => this.cancellationPolicy.set(policy)
    });
  }

  saveOverbookingPolicy(): void {
    const propertyId = this.property()?.id;
    if (!propertyId) return;
    this.service.updateHospitalityOverbookingPolicy(propertyId, this.overbookingForm).subscribe({
      next: policy => this.overbookingPolicy.set(policy)
    });
  }

  startEdit(): void { this.editing.set(true); }

  saveProperty(): void {
    this.saving.set(true);
    if (this.isCreateMode()) {
      this.service.createHospitalityProperty({
        propertyCode: this.form.propertyCode,
        name: this.form.name,
        address: this.form.address,
        timeZoneId: this.form.timeZoneId
      }).subscribe({
        next: created => {
          this.saving.set(false);
          void this.router.navigate(['/hospitality/properties', created.id]);
        },
        error: () => this.saving.set(false)
      });
      return;
    }

    const id = this.property()?.id;
    if (!id) return;

    this.service.updateHospitalityProperty(id, {
      name: this.form.name,
      address: this.form.address,
      timeZoneId: this.form.timeZoneId,
      isActive: this.form.isActive
    }).subscribe({
      next: updated => {
        this.property.set(updated);
        this.editing.set(false);
        this.saving.set(false);
      },
      error: () => this.saving.set(false)
    });
  }

  t(key: string): string { return this.localization.translate(key); }
}
